import type { FC } from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  FormControlLabel
} from '@mui/material';
import { taskService } from '@services/taskService';
import { employeeService } from '@services/employeeService';
import type { Task, Employee } from '@/types';
import { apiDateToHTML, htmlDateToAPI } from '@utils/dateUtils';
import toast from 'react-hot-toast';

interface TaskAuth {
  authorized: boolean;
  start_date: string;
  end_date: string;
  initial_hours: string;
  no_end_date: boolean;
}

interface AuthorizedTasksModalProps {
  show: boolean;
  onHide: () => void;
  employeeId: string;
  onSave: () => void;
}

const AuthorizedTasksModal: FC<AuthorizedTasksModalProps> = ({
  show,
  onHide,
  employeeId: initialEmployeeId,
  onSave,
}) => {
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>(initialEmployeeId);
  const [taskAuths, setTaskAuths] = useState<Record<string, TaskAuth>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      loadData();
    }
  }, [show, selectedEmployeeId]);

  const loadData = async () => {
    try {
      const [tasks, employeeList] = await Promise.all([
        taskService.getAllTasks(),
        employeeService.getAllEmployees(),
      ]);

      setAllTasks(tasks);
      setEmployees(employeeList);

      if (selectedEmployeeId) {
        const authorizedTasks = await taskService.getAuthorizedTasks(selectedEmployeeId);

        const auths: Record<string, TaskAuth> = {};
        tasks.forEach((task) => {
          const taskName = task.name.S;
          const authTask = authorizedTasks[taskName];

          if (authTask) {
            const endDate = authTask.M.end_date.N;
            auths[taskName] = {
              authorized: true,
              start_date: apiDateToHTML(authTask.M.start_date.N),
              end_date: endDate === '0' ? '' : apiDateToHTML(endDate),
              initial_hours: authTask.M.initial_hours.N,
              no_end_date: endDate === '0',
            };
          } else {
            auths[taskName] = {
              authorized: false,
              start_date: '',
              end_date: '',
              initial_hours: '0',
              no_end_date: false,
            };
          }
        });

        setTaskAuths(auths);
      }
    } catch (error) {
      console.error('Error loading authorized tasks:', error);
      toast.error('Failed to load authorized tasks');
    }
  };

  const handleAuthChange = (taskName: string, field: keyof TaskAuth, value: any) => {
    setTaskAuths((prev) => ({
      ...prev,
      [taskName]: {
        ...prev[taskName],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }

    setIsSaving(true);
    try {
      // Convert to API format
      const authorizedTaskMap: any = {};

      Object.entries(taskAuths).forEach(([taskName, auth]) => {
        if (auth.authorized) {
          authorizedTaskMap[taskName] = {
            M: {
              start_date: { N: htmlDateToAPI(auth.start_date) },
              end_date: { N: auth.no_end_date ? '0' : htmlDateToAPI(auth.end_date) },
              initial_hours: { N: auth.initial_hours || '0' },
            },
          };
        }
      });

      await taskService.saveAuthorizedTasks(selectedEmployeeId, authorizedTaskMap);
      toast.success('Authorized tasks saved successfully');
      onSave();
      onHide();
    } catch (error) {
      console.error('Error saving authorized tasks:', error);
      toast.error('Failed to save authorized tasks');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95%',
          maxWidth: '1400px',
        }
      }}
    >
      <DialogTitle>Select Authorized Tasks</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
          <InputLabel id="employee-select-label">Employee</InputLabel>
          <Select
            labelId="employee-select-label"
            id="employee-select"
            value={selectedEmployeeId}
            label="Employee"
            onChange={(e) => setSelectedEmployeeId(e.target.value)}
          >
            <MenuItem value="">Select an employee</MenuItem>
            {employees.map((employee) => (
              <MenuItem key={employee.employee_id.N} value={employee.employee_id.N}>
                {employee.name?.S || employee.email.S}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedEmployeeId && (
          <TableContainer component={Paper}>
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '100px' }}>Authorized</TableCell>
                  <TableCell>Task Name</TableCell>
                  <TableCell style={{ width: '150px' }}>Start Date</TableCell>
                  <TableCell style={{ width: '280px' }}>End Date</TableCell>
                  <TableCell style={{ width: '150px' }}>Initial Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTasks.map((task) => {
                  const taskName = task.name.S;
                  const auth = taskAuths[taskName] || {
                    authorized: false,
                    start_date: '',
                    end_date: '',
                    initial_hours: '0',
                    no_end_date: false,
                  };

                  return (
                    <TableRow key={taskName}>
                      <TableCell>
                        <Checkbox
                          checked={auth.authorized}
                          onChange={(e) =>
                            handleAuthChange(taskName, 'authorized', e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>{taskName}</TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          value={auth.start_date}
                          onChange={(e) =>
                            handleAuthChange(taskName, 'start_date', e.target.value)
                          }
                          disabled={!auth.authorized}
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <TextField
                            type="date"
                            value={auth.end_date}
                            onChange={(e) =>
                              handleAuthChange(taskName, 'end_date', e.target.value)
                            }
                            disabled={!auth.authorized || auth.no_end_date}
                            size="small"
                            style={{ flex: 1 }}
                            InputLabelProps={{ shrink: true }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={auth.no_end_date}
                                onChange={(e) =>
                                  handleAuthChange(taskName, 'no_end_date', e.target.checked)
                                }
                                disabled={!auth.authorized}
                              />
                            }
                            label="No end"
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          inputProps={{ step: 0.5 }}
                          value={auth.initial_hours}
                          onChange={(e) =>
                            handleAuthChange(taskName, 'initial_hours', e.target.value)
                          }
                          disabled={!auth.authorized}
                          size="small"
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onHide}>
          Close
        </Button>
        <Button onClick={handleSave} disabled={isSaving} variant="contained">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthorizedTasksModal;
